/** Mi panel: resumen del vendedor en pagina propia (design.md §22.3 y §22.7). */
import { MobileLayout } from '@/components/MobileLayout';
import { Avatar } from '@/components/Avatar';
import { KpiCard } from '@/components/KpiCard';
import { ProgressBar } from '@/components/ProgressBar';
import { SectionCard } from '@/components/SectionCard';
import { TrendBadge } from '@/components/TrendBadge';
import { BarSeriesChart, DonutChart } from '@/components/DashboardCharts';
import { donut, kpis, pipeline, topClients, weeklyBars } from '@/config/dashboardMock';
import { useMobileTasksQuery } from '@/hooks/useMobileTasks';
import { freshnessLabel } from './HomePage';

/** Bloque "Top clientes" del mockup: avatar, nombre, monto y variacion opcional. */
function TopClientsCard() {
  return (
    <SectionCard title="Top clientes">
      <ul className="divide-y divide-border-light">
        {topClients.map((client) => (
          <li key={client.name} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
            <Avatar name={client.name} size={40} />
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
              {client.name}
            </p>
            <div className="flex shrink-0 flex-col items-end gap-0.5">
              <span className="tabular-nums text-sm font-semibold text-text-heading">
                {client.value}
              </span>
              {client.delta !== undefined && (
                <TrendBadge
                  direction={client.delta >= 0 ? 'up' : 'down'}
                  value={Math.abs(client.delta)}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

export function DashboardPage() {
  const pendingQuery = useMobileTasksQuery('open');
  const pendingCount = (pendingQuery.data ?? []).length;
  const freshness = freshnessLabel(pendingQuery.dataUpdatedAt);

  return (
    <MobileLayout title="Mi panel" subtitle={freshness ?? undefined} showBack>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="px-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Resumen
          </h2>
          <span className="rounded-full bg-bg-tertiary px-2.5 py-1 text-xs font-medium text-text-secondary">
            Datos de ejemplo
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {kpis.map((kpi) => (
            <KpiCard
              key={kpi.key}
              icon={kpi.icon}
              label={kpi.label}
              value={kpi.value}
              trend={
                kpi.trend ? (
                  <TrendBadge direction={kpi.trend.direction} value={kpi.trend.value} />
                ) : undefined
              }
            />
          ))}
          <KpiCard icon="task_alt" label="Tareas pendientes" value={pendingCount} />
        </div>

        <SectionCard title="Pipeline comercial">
          {pipeline.map((step) => (
            <ProgressBar
              key={step.label}
              label={step.label}
              value={step.percent}
              tone={step.tone}
              className="mt-3 first:mt-0"
            />
          ))}
        </SectionCard>

        <SectionCard title="Ventas de la semana">
          <BarSeriesChart
            title={weeklyBars.title}
            legend={weeklyBars.legend}
            series={weeklyBars.series}
          />
        </SectionCard>

        <SectionCard title="Estado de cotizaciones">
          <DonutChart
            title={donut.title}
            centerValue={donut.centerValue}
            segments={donut.segments}
          />
        </SectionCard>

        <TopClientsCard />
      </div>
    </MobileLayout>
  );
}
